import {
  isAssessmentWelcomePath,
  resolveAssessmentSubmissionId,
  resolveAssessmentTestType,
} from "./assessmentEventLocation";

type AssessmentEventSession = {
  assessmentSessionId: string;
  testType: "reading" | "listening";
  submissionId: string | null;
};

const ASSESSMENT_EVENT_SESSION_STORAGE_KEY_PREFIX =
  "assessment_client_event_session";

function generateAssessmentSessionId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `assessment-session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function buildAssessmentEventSessionStorageKey(
  userId: string | null | undefined,
): string | null {
  const normalizedUserId = userId?.trim();
  if (!normalizedUserId) {
    return null;
  }

  return `${ASSESSMENT_EVENT_SESSION_STORAGE_KEY_PREFIX}:${normalizedUserId}`;
}

function readStoredAssessmentSession(
  userId: string | null | undefined,
): AssessmentEventSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  const storageKey = buildAssessmentEventSessionStorageKey(userId);
  if (!storageKey) {
    return null;
  }

  const rawSession = window.sessionStorage.getItem(storageKey);
  if (!rawSession) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawSession) as AssessmentEventSession;
    if (
      !parsed?.assessmentSessionId ||
      !parsed?.testType ||
      !["reading", "listening"].includes(parsed.testType)
    ) {
      return null;
    }

    return {
      assessmentSessionId: parsed.assessmentSessionId,
      testType: parsed.testType,
      submissionId: parsed.submissionId?.trim() || null,
    };
  } catch {
    return null;
  }
}

function writeStoredAssessmentSession(
  userId: string | null | undefined,
  session: AssessmentEventSession,
): AssessmentEventSession {
  if (typeof window !== "undefined") {
    const storageKey = buildAssessmentEventSessionStorageKey(userId);
    if (storageKey) {
      window.sessionStorage.setItem(storageKey, JSON.stringify(session));
    }
  }

  return session;
}

export function resolveAssessmentSessionId(
  userId: string | null | undefined,
  pageLocation: string,
): string | null {
  const normalizedUserId = userId?.trim();
  const testType = resolveAssessmentTestType(pageLocation);
  if (!testType || !normalizedUserId) {
    return null;
  }

  const submissionId = resolveAssessmentSubmissionId(pageLocation);
  const storedSession = readStoredAssessmentSession(normalizedUserId);
  const shouldStartFreshSession = isAssessmentWelcomePath(pageLocation);

  if (shouldStartFreshSession) {
    return writeStoredAssessmentSession(normalizedUserId, {
      assessmentSessionId: generateAssessmentSessionId(),
      testType,
      submissionId,
    }).assessmentSessionId;
  }

  if (!storedSession || storedSession.testType !== testType) {
    return writeStoredAssessmentSession(normalizedUserId, {
      assessmentSessionId: generateAssessmentSessionId(),
      testType,
      submissionId,
    }).assessmentSessionId;
  }

  if (storedSession.submissionId !== submissionId) {
    return writeStoredAssessmentSession(normalizedUserId, {
      ...storedSession,
      submissionId,
    }).assessmentSessionId;
  }

  return storedSession.assessmentSessionId;
}
